import { useApiClient } from '@/composables/useApiClient'
import { PlatformSource } from '@/view-models/platform'

const loadEventNews = (sheetData, locale) => {
  const lines = sheetData.split(/\r?\n/)
  const localeMapping = {
    'zh-tw': 0,
    'zh-cn': 2,
    ja: 3,
    en: 4,
  }

  const activities = lines
    .map((line, i) => {
      // 第一行說明內容,不處理
      if (i === 0) {
        return false
      }
      // 資料以,隔開
      // 連結名稱,網址,簡中,日文,英文
      const data = line.split(',')
      if (data[0] === '' || data[1] === '') {
        return false
      }

      const colIdx = localeMapping[locale] || 0
      const title = data[colIdx].trim()
      const link = data[1].trim()

      return {
        title: title,
        link: link,
      }
    })
    .filter(Boolean)

  if (sessionStorage.getItem('Activities') === null) {
    sessionStorage.setItem('Activities', JSON.stringify(activities))
  }

  return activities
}

export const state = () => ({
  activities: [],
  userIpAddress: '127.0.0.1',
  events: [],
  menuNavigation: false,
  userNavigation: false,
  searchNavigation: false,
  groupOrderStatus: false,
  groupOrder: {},
  headerHeight: 64,
  hideHeaderItems: false,
  isEmbeddedSearchBarVisible: false,
  hideFooter: false,
  foreignDomainFromCathay: false,
  foreignDomainFromMichelin: false,
  foreignDomainFromLine: false,
  isBookingEngine: false,
  tenantName: '',
  fromTngMiniProgram: false,
  // TODO: '@/assets/images/logo/logo.svg'
  funnowLogoImage: '/images/logo/logo.svg',
})

/** @type {import('vuex').GetterTree<any,any>} */
export const getters = {
  activities: state => state.activities,
  userIpAddress: state => state.userIpAddress,
  events: state => state.events,
  headerHeight: state => state.headerHeight,
  menuNavigation: state => state.menuNavigation,
  userNavigation: state => state.userNavigation,
  searchNavigation: state => state.searchNavigation,
  hideHeaderItems: state => state.hideHeaderItems,
  isEmbeddedSearchBarVisible: state => state.isEmbeddedSearchBarVisible,
  hideFooter: state => state.hideFooter,
  groupOrderStatus: state => state.groupOrderStatus,
  groupOrder: state => state.groupOrder,
  foreignDomainFromCathay: state => state.foreignDomainFromCathay,
  foreignDomainFromMichelin: state => state.foreignDomainFromMichelin,
  isFromForeignDomain: state => !!state.foreignDomainFromCathay || !!state.foreignDomainFromMichelin,
  foreignDomainFromLine: state => state.foreignDomainFromLine,
  isBookingEngine: state => state.isBookingEngine,
  tenantName: state => state.tenantName,
  fromTngMiniProgram: state => state.fromTngMiniProgram,
  /**
   * @description 較早開發的平台，相關參數。後來的平台在 store-pinia/platform.js
   */
  platformSourceFrom: (_, getters) => {
    // 米其林在過往的 code，一直都沒有帶 from 米其林，故先帶 FunNow，避免後端有不如預期的 side effect
    if (getters.foreignDomainFromMichelin) {
      return PlatformSource.FunNow
    }
    if (getters.isBookingEngine) {
      return PlatformSource.BookingEngine
    }
    return PlatformSource.FunNow
  },
}

export const actions = {
  /**
   * @param {import('vuex').ActionContext}
   * @param {import('@nuxt/types').Context}
   */
  async nuxtServerInit({ dispatch, commit }, { route, req, params, store, error }) {
    try {
      await dispatch('region/getAllRegion')
      await dispatch('currency/getCurrency')

      const ipAddress = req?.headers['x-forwarded-for']?.split(',').pop() || req?.connection.remoteAddress
      commit('SET_USER_IP_ADDRESS', ipAddress)

      if (req.headers.cookie) {
        const lineSID = useNuxtApp().$cookiz.get('Line_SID')

        if (lineSID) {
          commit('SET_FOREGIN_DOMAIN_FROM_LINE', true)
        }
      }

      if (route.query.from === 'tng') {
        commit('SET_FROM_TNG_MINI_PROGRAM', true)
      }
    } catch (err) {
      console.error('nuxtServerInit:error', err.message)
      error({ statusCode: 404, message: 'Post not found' })
    }
  },
  setUserIpAddress({ commit }, ip) {
    commit('SET_USER_IP_ADDRESS', ip)
  },
  setEvents({ commit }, events) {
    commit('SET_EVENTS', events)
  },
  setHeaderHeight({ commit }, height) {
    commit('SET_HEADER_HEIGHT', height)
  },
  setHideHeaderItems({ commit }, status) {
    commit('SET_HIDE_HEADER_ITEMS', status)
  },
  setEmbeddedSearchBarVisible({ commit }, val) {
    commit('SET_EMBEDDED_SEARCH_BAR_VISIBLE', val)
  },
  setHideFooter({ commit }, status) {
    commit('SET_HIDE_FOOTER', status)
  },
  setMenuNavigation({ commit }, state) {
    commit('SET_MENU_NAVIGATION', state)
  },
  setUserNavigation({ commit }, state) {
    commit('SET_USER_NAVIGATION', state)
  },
  setSearchNavigation({ commit }, state) {
    commit('SET_SEARCH_NAVIGATION', state)
  },
  setGroupOrder({ commit }, groupOrder = {}) {
    commit('SET_GROUP_ORDER_STATUS', groupOrder)
    commit('SET_GROUP_ORDER', groupOrder)
  },
  clearGroupOrder({ commit }) {
    commit('SET_GROUP_ORDER_STATUS', false)
    commit('SET_GROUP_ORDER', {})
  },
  setForeignDomainFromCathay({ commit }, cathayStatus) {
    commit('SET_FOREGIN_DOMAIN_FROM_CATHAY', cathayStatus)
  },
  setForeignDomainFromMichelin({ commit }, michelinStatus) {
    commit('SET_FOREGIN_DOMAIN_FROM_MICHELIN', michelinStatus)
  },
  // unused function
  getEventSheet({ commit }) {
    if (sessionStorage.getItem('Activities')) {
      const activities = JSON.parse(sessionStorage.getItem('Activities'))
      commit('SET_ACTIVITIES', activities)
    } else {
      const eventSheet = 'https://cdn.myfunnow.com/web/csv/event_20200630.csv'
      fetch(eventSheet, { credentials: 'omit' })
        .then(res => {
          return res.text()
        })
        .then(data => {
          const activities = loadEventNews(data)
          commit('SET_ACTIVITIES', activities)
        })
    }
  },
}

export const mutations = {
  SET_USER_IP_ADDRESS(state, ip) {
    state.userIpAddress = ip
  },
  SET_EVENTS(state, events) {
    state.events = events
  },
  SET_HEADER_HEIGHT(state, height = 64) {
    state.headerHeight = height
  },
  SET_HIDE_HEADER_ITEMS(state, status) {
    state.hideHeaderItems = status
  },
  SET_EMBEDDED_SEARCH_BAR_VISIBLE(state, val) {
    state.isEmbeddedSearchBarVisible = val
  },
  SET_HIDE_FOOTER(state, status) {
    state.hideFooter = status
  },
  SET_MENU_NAVIGATION(state, menuState) {
    state.menuNavigation = menuState
  },
  SET_USER_NAVIGATION(state, menuState) {
    state.userNavigation = menuState
  },
  SET_SEARCH_NAVIGATION(state, navigationState) {
    state.searchNavigation = navigationState
  },
  SET_GROUP_ORDER_STATUS(state, groupOrder = {}) {
    state.groupOrderStatus = Boolean(groupOrder.group_code && groupOrder.booking_time)
  },
  SET_GROUP_ORDER(state, groupOrder = {}) {
    state.groupOrder = groupOrder
  },
  SET_FOREGIN_DOMAIN_FROM_CATHAY(state, cathayStatus) {
    state.foreignDomainFromCathay = cathayStatus
  },
  SET_FOREGIN_DOMAIN_FROM_MICHELIN(state, michelinStatus) {
    state.foreignDomainFromMichelin = michelinStatus
  },
  SET_FOREGIN_DOMAIN_FROM_LINE(state, status) {
    state.foreignDomainFromLine = status
  },
  SET_IS_BOOKING_ENGINE(state, boolean) {
    state.isBookingEngine = boolean
  },
  SET_TENANT_NAME(state, name) {
    state.tenantName = name
  },
  SET_ACTIVITIES(state, activities) {
    state.activities = activities
  },
  SET_FROM_TNG_MINI_PROGRAM(state, status) {
    state.fromTngMiniProgram = status
  },
}
